import { Fragment, type ReactNode } from 'react';

const INLINE_MARKDOWN = /(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\([^)]+\))/g;

function safeExternalUrl(value: string): string | null {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
}

function inlineContent(value: string): ReactNode[] {
  return value.split(INLINE_MARKDOWN).filter(Boolean).map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*')) {
      return <em key={index}>{part.slice(1, -1)}</em>;
    }
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      const href = safeExternalUrl(link[2]);
      return href
        ? <a key={index} href={href} target="_blank" rel="noopener noreferrer" className="underline" style={{ color: 'var(--app-accent)' }}>{link[1]}</a>
        : <Fragment key={index}>{link[1]}</Fragment>;
    }
    return <Fragment key={index}>{part}</Fragment>;
  });
}

export default function HomeSectionMarkdown({ markdown }: { markdown: string }) {
  const lines = markdown.split(/\r?\n/);
  const blocks: ReactNode[] = [];
  let listItems: string[] = [];

  const flushList = () => {
    if (!listItems.length) return;
    blocks.push(
      <ul key={`list-${blocks.length}`} className="list-disc space-y-1 pl-5">
        {listItems.map((item, index) => <li key={index}>{inlineContent(item)}</li>)}
      </ul>,
    );
    listItems = [];
  };

  lines.forEach((line) => {
    const listItem = line.match(/^\s*-\s+(.+)$/);
    if (listItem) {
      listItems.push(listItem[1]);
      return;
    }
    flushList();
    if (line.trim()) {
      blocks.push(<p key={`paragraph-${blocks.length}`}>{inlineContent(line)}</p>);
    }
  });
  flushList();

  return <div className="space-y-2 text-sm leading-6" style={{ color: 'var(--app-text-muted)' }}>{blocks}</div>;
}
